import { jsPDF } from "jspdf";
import { downloadBlob } from "./vehicleImage";

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatTripDate(trip) {
  const value =
    trip?.__startDate ||
    trip?.trip_start_time ||
    trip?.startTime ||
    trip?.start_time;
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function exportStatsToXls({
  deviceId,
  rangeLabel,
  filteredTrips,
  metrics,
  avgTripDistance,
  totalDriveHours,
}) {
  const rows = filteredTrips.map((trip, index) => {
    const distance = metrics.distances[index] ?? 0;
    const speed = metrics.speeds[index] ?? 0;
    const duration = trip?.durationMinutes ?? trip?.duration_minutes ?? 0;
    return `
      <Row>
        <Cell><Data ss:Type="String">${escapeXml(formatTripDate(trip))}</Data></Cell>
        <Cell><Data ss:Type="Number">${distance}</Data></Cell>
        <Cell><Data ss:Type="Number">${speed}</Data></Cell>
        <Cell><Data ss:Type="Number">${duration}</Data></Cell>
      </Row>`;
  });

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Statistics">
    <Table>
      <Row>
        <Cell><Data ss:Type="String">Device</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(deviceId)}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Range</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(rangeLabel)}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Total Distance (km)</Data></Cell>
        <Cell><Data ss:Type="Number">${metrics.totalDistance}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Average Speed (km/h)</Data></Cell>
        <Cell><Data ss:Type="Number">${metrics.avgSpeed}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Trips</Data></Cell>
        <Cell><Data ss:Type="Number">${filteredTrips.length}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Avg Trip Distance (km)</Data></Cell>
        <Cell><Data ss:Type="Number">${avgTripDistance}</Data></Cell>
      </Row>
      <Row>
        <Cell><Data ss:Type="String">Total Drive (hours)</Data></Cell>
        <Cell><Data ss:Type="Number">${totalDriveHours}</Data></Cell>
      </Row>
      <Row></Row>
      <Row>
        <Cell><Data ss:Type="String">Start Time</Data></Cell>
        <Cell><Data ss:Type="String">Distance (km)</Data></Cell>
        <Cell><Data ss:Type="String">Avg Speed (km/h)</Data></Cell>
        <Cell><Data ss:Type="String">Duration (min)</Data></Cell>
      </Row>
      ${rows.join("")}
    </Table>
  </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  downloadBlob(blob, `statistics-${deviceId || "vehicle"}.xls`);
}

export function exportStatsToPdf({
  deviceId,
  rangeLabel,
  filteredTrips,
  metrics,
  avgTripDistance,
  totalDriveHours,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const margin = 14;
  let y = margin;

  const addLine = (text, size = 10, gap = 6) => {
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(String(text), 180);
    lines.forEach((line) => {
      if (y > 280) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += gap;
    });
  };

  addLine("Vehicle Statistics Report", 16, 8);
  addLine(`Device: ${deviceId || "N/A"}`, 11);
  addLine(`Range: ${rangeLabel}`, 11);
  addLine(`Total Distance: ${metrics.totalDistance} km`, 11);
  addLine(`Average Speed: ${metrics.avgSpeed} km/h`, 11);
  addLine(`Trips: ${filteredTrips.length}`, 11);
  addLine(`Avg Trip Distance: ${avgTripDistance} km`, 11);
  addLine(`Total Drive: ${totalDriveHours} hours`, 11);
  y += 4;
  addLine("Trip Details", 12, 8);

  filteredTrips.forEach((trip, index) => {
    const distance = metrics.distances[index] ?? 0;
    const speed = metrics.speeds[index] ?? 0;
    const duration = trip?.durationMinutes ?? trip?.duration_minutes ?? 0;
    addLine(
      `${formatTripDate(trip)} | ${distance} km | ${speed} km/h | ${duration} min`,
      9,
      5
    );
  });

  const pdfBlob = doc.output("blob");
  downloadBlob(pdfBlob, `statistics-${deviceId || "vehicle"}.pdf`);
}
